import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/shared/lib/api";
import { useAuth } from "@/shared/context/AuthContext";
import { Calendar, Clock, MapPin, ArrowRight, ChevronRight, Sparkles } from "lucide-react";
import BottomTabBar from "@/apps/rider/components/BottomTabBar";
import { formatTime12 } from "@/shared/lib/format";
import Skeleton from "@/shared/motion/Skeleton";
import RouteMap from "@/shared/components/RouteMap";

/**
 * /trips — rider-facing live trips screen.
 *
 * Layout: floating cards on a soft surface, no boxed dashboards.
 *  · Upcoming card    — primary, prominent, with status pill + driver slots
 *  · History list     — minimal date-grouped strip below
 *  · Empty state      — "No rides scheduled" + CTA → /schedule
 *
 * Status states the UI is designed to show (UI-ready even if backend always
 * returns "scheduled" today). Brand colour is preserved per state.
 */
const STATUS = {
  scheduled:        { label: "Scheduled",        tone: "neutral",  pulse: false },
  driver_assigned:  { label: "Driver assigned",  tone: "info",     pulse: false },
  driver_en_route:  { label: "Driver en route",  tone: "info",     pulse: true  },
  arriving_soon:    { label: "Arriving soon",    tone: "accent",   pulse: true  },
  in_progress:      { label: "Ride in progress", tone: "success",  pulse: true  },
  completed:        { label: "Completed",        tone: "muted",    pulse: false },
  cancelled:        { label: "Cancelled",        tone: "muted",    pulse: false },
};
const TONE_CLS = {
  neutral: "bg-[#0A1128]/85 text-[#F9F7F4] border-[#0A1128]",
  info:    "bg-[#0074d4] text-white border-[#0074d4]",
  accent:  "bg-[#ed6500] text-white border-[#ed6500]",
  success: "bg-emerald-600 text-white border-emerald-600",
  muted:   "bg-[#515E7B]/20 text-[#515E7B] border-[#515E7B]/30",
};

function StatusPill({ status }) {
  const meta = STATUS[status] || STATUS.scheduled;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] tracking-widest uppercase font-semibold border rounded-full ${TONE_CLS[meta.tone]}`}
      data-testid={`trip-status-${status}`}
    >
      {meta.pulse && (
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inset-0 inline-flex h-full w-full rounded-full bg-current opacity-60 animate-ping" />
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-current" />
        </span>
      )}
      {meta.label}
    </span>
  );
}

/* ---------- Ride status stepper (B1 core) --------------------------------
 * A four-step progress track that surfaces WHERE the trip currently sits in
 * the lifecycle. Renders BENEATH the status pill inside UpcomingCard so a
 * rider can see the pill-copy AND the wider journey at a glance.
 *
 * Steps: Scheduled → Driver assigned → En route → Arriving → In progress → Completed.
 *
 * `activeIndex` derives from the current status. Every step past `activeIndex`
 * is dimmed; the active step is highlighted with the same orange used across
 * the brand; every step before it is a filled check.
 *
 * A11y: the stepper renders as an ordered-list with aria-current on the
 * active step (Phase B4 will layer in aria-live announcements at status
 * transitions; the DOM shape here is compatible).
 * ------------------------------------------------------------------------- */
const RIDE_STATUS_STEPS = [
  { id: "scheduled",       label: "Scheduled",   matches: ["scheduled", "driver_assigned"] },
  { id: "driver_en_route", label: "En route",    matches: ["driver_en_route"] },
  { id: "arriving_soon",   label: "Arriving",    matches: ["arriving_soon"] },
  { id: "in_progress",     label: "In progress", matches: ["in_progress"] },
  { id: "completed",       label: "Completed",   matches: ["completed"] },
];

function statusIndex(status) {
  const idx = RIDE_STATUS_STEPS.findIndex((s) => s.matches.includes(status));
  return idx >= 0 ? idx : 0;
}

function RideStatusStepper({ status }) {
  const idx = statusIndex(status);
  const isCancelled = status === "cancelled";
  return (
    <ol
      role="list"
      className="relative mt-5 grid grid-cols-5 gap-1"
      data-testid="ride-status-stepper"
      aria-label="Ride progress"
    >
      {RIDE_STATUS_STEPS.map((step, i) => {
        const isDone = !isCancelled && i < idx;
        const isActive = !isCancelled && i === idx;
        const isUpcoming = isCancelled || i > idx;
        return (
          <li
            key={step.id}
            {...(isActive ? { "aria-current": "step" } : {})}
            className="flex flex-col items-center gap-1.5 min-w-0"
            data-testid={`ride-status-step-${step.id}`}
            data-step-state={isDone ? "done" : isActive ? "active" : "upcoming"}
          >
            <div className="w-full flex items-center gap-1">
              {/* Track segment to the LEFT of the node (skipped for the
                  first step). The `done` variant fills fully; `active`
                  reveals a subtle orange gradient; upcoming is dim. */}
              {i > 0 && (
                <div
                  className={`h-0.5 flex-1 rounded-full ${
                    isDone || isActive
                      ? "bg-[#ed6500]"
                      : "bg-[#F9F7F4]/15"
                  }`}
                  aria-hidden="true"
                />
              )}
              <div
                className={`w-2.5 h-2.5 rounded-full shrink-0 transition-transform ${
                  isDone
                    ? "bg-[#ed6500]"
                    : isActive
                      ? "bg-[#ed6500] ring-4 ring-[#ed6500]/25 scale-110"
                      : "bg-[#F9F7F4]/25"
                }`}
                aria-hidden="true"
              />
              {/* Track segment to the RIGHT (skipped for the last step). */}
              {i < RIDE_STATUS_STEPS.length - 1 && (
                <div
                  className={`h-0.5 flex-1 rounded-full ${
                    isDone
                      ? "bg-[#ed6500]"
                      : "bg-[#F9F7F4]/15"
                  }`}
                  aria-hidden="true"
                />
              )}
            </div>
            <div
              className={`text-[9px] tracking-widest uppercase text-center leading-tight ${
                isActive
                  ? "text-[#ed6500] font-semibold"
                  : isDone
                    ? "text-[#F9F7F4]/75"
                    : "text-[#F9F7F4]/45"
              }`}
            >
              {step.label}
            </div>
          </li>
        );
      })}
      {isCancelled && (
        <li
          className="col-span-5 mt-2 text-center text-[10px] tracking-widest uppercase text-[#F9F7F4]/55"
          data-testid="ride-status-cancelled"
        >
          Ride cancelled
        </li>
      )}
    </ol>
  );
}

/* ---------- helpers ---------- */
const SHORT = (a) => (a ? a.split(",")[0].trim() : "");

function isUpcoming(t) {
  // anything that's "scheduled"-ish today/in future
  if (!t?.date) return false;
  const d = t.date; // ISO yyyy-mm-dd
  const today = new Date();
  const ymd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2,"0")}-${String(today.getDate()).padStart(2,"0")}`;
  return d >= ymd && (t.status || "scheduled") !== "completed" && (t.status || "scheduled") !== "cancelled";
}

function dayName(iso) {
  // Delegate to the centralised explicit-date formatter (Phase A tail #2).
  // Always renders "Weekday, Mon DD" with the full year appended so a
  // receipt or shared trip link is self-describing.
  const [y, mo, d] = String(iso).split("-").map(Number);
  const dt = new Date(y, mo - 1, d, 12, 0, 0, 0);
  return dt.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric", year: "numeric" });
}

function groupByDate(arr) {
  const map = new Map();
  for (const t of arr) {
    if (!t.date) continue;
    if (!map.has(t.date)) map.set(t.date, []);
    map.get(t.date).push(t);
  }
  return Array.from(map.entries()).sort((a,b) => b[0].localeCompare(a[0]));
}

/* =========================================================================
   PAGE
   ========================================================================= */
export default function Trips() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [trips, setTrips] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (user === false || !user) { nav("/account"); return; }
    let cancelled = false;
    (async () => {
      try {
        const [r, t] = await Promise.all([api.get("/routes/me"), api.get("/trips/me")]);
        if (cancelled) return;
        setRoutes(r.data || []);
        setTrips(t.data || []);
      } catch (_) {} finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, authLoading, nav]);

  // Poll the live upcoming-trip endpoint every 30s so the rider sees driver
  // assignment + ETA updates without having to refresh. Polling pauses when
  // the tab is hidden so we don't burn battery on idle phones.
  const [live, setLive] = useState(null);
  useEffect(() => {
    if (!user) return;
    let stopped = false;
    const tick = async () => {
      if (document.hidden) return;
      try {
        const { data } = await api.get("/me/trip/upcoming");
        if (!stopped) setLive(data || null);
      } catch (_) {}
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => { stopped = true; clearInterval(id); };
  }, [user]);

  // Active-trip live tracking — when the upcoming trip has a driver assigned
  // AND the status is past "scheduled" we poll /trips/{id}/live every 4 s for
  // the driver's GPS fix + status. We tear the interval down on unmount /
  // status==completed so we don't burn battery once the ride ends.
  const [trackingState, setTrackingState] = useState(null); // {status, driver_location}
  const trackedRef = useRef(null);
  const liveTrip = live?.trip || null;
  const liveDriverId = liveTrip?.driver_id;
  const liveStatus = liveTrip?.status;
  useEffect(() => {
    if (!liveTrip || !liveDriverId) { setTrackingState(null); return; }
    if (["scheduled", "completed", "cancelled"].includes(liveStatus)) {
      setTrackingState(null);
      return;
    }
    if (trackedRef.current === liveTrip.id) return;
    trackedRef.current = liveTrip.id;
    let stopped = false;
    const tick = async () => {
      if (document.hidden) return;
      try {
        const { data } = await api.get(`/trips/${liveTrip.id}/live`);
        if (!stopped) setTrackingState(data);
        if (data?.status === "completed") stopped = true;
      } catch (_) {}
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => { stopped = true; trackedRef.current = null; clearInterval(id); };
  }, [liveTrip, liveDriverId, liveStatus]);

  const routeById = useMemo(() => {
    const m = new Map();
    for (const r of routes) m.set(r.id, r);
    return m;
  }, [routes]);

  const upcoming = useMemo(() => {
    // Prefer the live-polled trip (it carries fresh status + driver fields);
    // fall back to the static /trips/me result on first render.
    if (live?.trip) return live.trip;
    const all = trips.filter(isUpcoming).sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return (a.pickup_time || "").localeCompare(b.pickup_time || "");
    });
    return all[0] || null;
  }, [trips, live]);

  const liveRoute = live?.route || null;

  const history = useMemo(() => {
    const past = trips.filter((t) => !isUpcoming(t)).sort((a,b) => b.date.localeCompare(a.date));
    return groupByDate(past).slice(0, 12);
  }, [trips]);

  if (authLoading || loading) {
    // Skeleton scaffold that matches the eventual layout (header + big
    // upcoming card + timeline rows) — a rider glancing at their phone
    // during transit sees "something is loading" instead of the old
    // "Loading…" text bubble which looked like an error state.
    return (
      <div className="fixed inset-0 flex flex-col bg-[#F9F7F4]" data-testid="trips-page-loading">
        <div className="flex-1 min-h-0 overflow-y-auto wheel-scroll">
          <div
            className="max-w-2xl mx-auto px-4 sm:px-6 pb-[max(env(safe-area-inset-bottom),96px)]"
            style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 84px)" }}
          >
            <div className="mb-5 space-y-2">
              <Skeleton className="h-3 w-24" testid="trips-skeleton-eyebrow" />
              <Skeleton className="h-9 w-40" testid="trips-skeleton-title" />
            </div>
            <Skeleton className="h-56 w-full rounded-3xl mb-6" testid="trips-skeleton-upcoming" />
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-16 w-full rounded-2xl" testid={`trips-skeleton-row-${i}`} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col" style={{ background: "var(--dusk-bg)" }} data-testid="trips-page" data-dusk="1">
      <div className="flex-1 min-h-0 overflow-y-auto wheel-scroll">
        <div
          className="max-w-2xl mx-auto px-4 sm:px-6 pb-[max(env(safe-area-inset-bottom),96px)]"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 84px)" }}
        >
        <header className="mb-5">
          <div className="label-xs text-[#ed6500]">Your rides</div>
          <h1 className="font-serif font-light text-3xl sm:text-4xl leading-tight text-[#0A1128] mt-1">
            Trips
          </h1>
        </header>

        {!upcoming && (
          <EmptyState onBook={() => nav("/schedule")} />
        )}

        {upcoming && trackingState && trackingState.driver_location && (
          <LiveTrackingMap
            driverLocation={trackingState.driver_location}
            polyline={(liveRoute || routeById.get(upcoming.route_id))?.encoded_polyline}
            status={trackingState.status}
          />
        )}

        {upcoming && (
          <UpcomingCard
            trip={trackingState ? { ...upcoming, status: trackingState.status } : upcoming}
            route={liveRoute || routeById.get(upcoming.route_id)}
          />
        )}

        {history.length > 0 && (
          <HistorySection groups={history} routeById={routeById} />
        )}
        </div>
      </div>
      <BottomTabBar active="trips" />
    </div>
  );
}

/* ---------- live tracking strip ---------- */
function LiveTrackingMap({ driverLocation, polyline, status }) {
  const center = useMemo(() => ({
    lat: driverLocation?.lat || 39.0997,
    lng: driverLocation?.lng || -94.5786,
  }), [driverLocation?.lat, driverLocation?.lng]);

  const STATUS_COPY = {
    driver_en_route: "Driver en route",
    arriving_soon:   "Arriving now",
    in_progress:     "Ride in progress",
  };

  return (
    <div className="mb-4 rounded-3xl overflow-hidden shadow-lg border hairline" data-testid="trip-live-map">
      <div className="relative h-56 sm:h-64">
        <RouteMap
          center={center}
          polyline={polyline || ""}
          userLocation={driverLocation ? { lat: driverLocation.lat, lng: driverLocation.lng } : null}
          fallbackSrc=""
        />
        <div className="absolute top-3 left-3 right-3 flex items-center justify-between gap-2 pointer-events-none">
          <span className="bg-[#0A1128] text-[#F9F7F4] px-3 py-1 rounded-full text-[10px] tracking-widest uppercase font-semibold inline-flex items-center gap-1.5 shadow">
            <span className="w-1.5 h-1.5 rounded-full bg-[#ed6500] animate-pulse" />
            {STATUS_COPY[status] || "Live"}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ---------- empty state ---------- */
function EmptyState({ onBook }) {
  return (
    <div
      className="p-8 text-center rounded-3xl"
      style={{
        background: "var(--dusk-glass)",
        backdropFilter: "var(--dusk-glass-blur)",
        WebkitBackdropFilter: "var(--dusk-glass-blur)",
        boxShadow: "var(--dusk-glass-border)",
      }}
      data-testid="trips-empty"
    >
      <div
        className="mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-3"
        style={{ background: "rgba(237,101,0,0.15)" }}
      >
        <Sparkles size={20} className="text-[#ed6500]" />
      </div>
      <h2 className="font-serif text-2xl" style={{ color: "var(--dusk-text)" }}>
        No rides scheduled
      </h2>
      <p className="text-sm mt-1" style={{ color: "var(--dusk-text-muted)" }}>
        Book a recurring weekly route — pickup, return, every week.
      </p>
      <button
        onClick={onBook}
        className="text-[11px] tracking-widest uppercase mt-5 inline-flex items-center gap-2 rounded-full font-semibold"
        style={{
          background: "var(--dusk-cta)",
          color: "#fff",
          padding: "12px 22px",
          boxShadow: "var(--dusk-cta-shadow)",
        }}
        data-testid="trips-book-btn"
      >
        Book a ride <ArrowRight size={14} />
      </button>
    </div>
  );
}

/* ---------- upcoming card ---------- */
function UpcomingCard({ trip, route }) {
  const status = trip.status || "scheduled";
  const driver = trip.driver || null; // future-ready slot
  const eta = trip.driver_eta_minutes; // future-ready
  const tier = route?.tier || trip.tier;

  return (
    <section
      className="relative bg-[#0A1128] text-[#F9F7F4] p-5 sm:p-6 shadow-lg overflow-hidden rounded-3xl"
      data-testid="trip-upcoming-card"
    >
      {/* subtle orange glow when active — softened to a light dust in dark
          mode so it doesn't muddy the elevated card surface. */}
      <div
        className="absolute -inset-px pointer-events-none trip-glow"
        aria-hidden="true"
      />

      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="label-xs text-[#F9F7F4]/60">Next ride</div>
          <div className="font-serif text-2xl sm:text-3xl mt-0.5 truncate" data-testid="trip-date">
            {dayName(trip.date)}
          </div>
        </div>
        <StatusPill status={status} />
      </div>

      {/* Ride status stepper — shows the lifecycle position at a glance so
          the rider never has to guess how far along their trip is. Hidden
          for `completed` + `cancelled` on trips that have already ended
          (nothing to progress); still shown for in-flight / upcoming. */}
      {status !== "completed" && <RideStatusStepper status={status} />}

      {/* time + tier */}
      <div className="relative mt-4 flex items-center gap-4 text-sm">
        <div className="flex items-center gap-1.5">
          <Clock size={14} className="text-[#ed6500]" />
          <span className="font-semibold">{formatTime12(trip.pickup_time)}</span>
        </div>
        {tier && (
          <div className="flex items-center gap-1.5 text-[#F9F7F4]/70">
            <span className="w-1 h-1 rounded-full bg-[#0074d4]" />
            <span className="font-serif">{tier.name || tier}</span>
          </div>
        )}
      </div>

      {/* route */}
      <div className="relative mt-4 grid gap-1.5">
        <div className="flex items-center gap-2 text-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-[#0074d4] shrink-0" />
          <span className="truncate">{SHORT(trip.pickup_address || route?.pickup_address)}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-[#ed6500] shrink-0" />
          <span className="truncate">{SHORT(trip.dropoff_address || trip.destination_address || route?.destination_address)}</span>
        </div>
      </div>

      {/* driver block — UI-ready, gently asks the user to wait when empty */}
      <div className="relative mt-5 pt-4 border-t border-[#F9F7F4]/15" data-testid="trip-driver-block">
        {driver ? (
          <div className="flex items-center gap-3">
            {driver.photo_url ? (
              <img src={driver.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-[#F9F7F4]/10 flex items-center justify-center text-xs">
                {(driver.name || "D").slice(0,1)}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">{driver.name || "Your driver"}</div>
              <div className="text-xs text-[#F9F7F4]/70 truncate">
                {[driver.vehicle, driver.license_plate].filter(Boolean).join(" · ") || "—"}
              </div>
            </div>
            {eta != null && (
              <div className="text-right">
                <div className="label-xs text-[#ed6500]">ETA</div>
                <div className="font-serif text-lg leading-none mt-0.5">{eta} min</div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs text-[#F9F7F4]/55">
            Driver details appear here as soon as your trip is assigned.
          </div>
        )}
      </div>
    </section>
  );
}

/* ---------- history ---------- */
function HistorySection({ groups, routeById }) {
  return (
    <section className="mt-10" data-testid="trip-history">
      <h2 className="label-xs text-[#515E7B] mb-3">History</h2>
      <div className="space-y-5">
        {groups.map(([date, items]) => (
          <div key={date}>
            <div className="text-[11px] tracking-widest uppercase text-[#515E7B] mb-2 flex items-center gap-2">
              <Calendar size={11} className="text-[#ed6500]" />
              {dayName(date)}
            </div>
            <div className="space-y-1">
              {items.map((t) => {
                const r = routeById.get(t.route_id);
                return (
                  <div
                    key={t.id}
                    className="glass p-3 flex items-center gap-3 text-sm rounded-2xl"
                    data-testid="trip-history-row"
                  >
                    <Clock size={14} className="text-[#515E7B] shrink-0" />
                    <span className="font-semibold w-16 shrink-0">{formatTime12(t.pickup_time)}</span>
                    <div className="flex-1 min-w-0 flex items-center gap-2 truncate">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#0074d4] shrink-0" />
                      <span className="truncate text-[#0A1128]">{SHORT(t.pickup_address || r?.pickup_address)}</span>
                      <ArrowRight size={11} className="text-[#515E7B] shrink-0" />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#ed6500] shrink-0" />
                      <span className="truncate text-[#0A1128]">{SHORT(t.dropoff_address || t.destination_address || r?.destination_address)}</span>
                    </div>
                    <ChevronRight size={14} className="text-[#515E7B]/60 shrink-0" />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
